import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import HotRank from '@/pages/home/components/HotRank';
import { formatViews } from '@/components/feature/NewsCard';
import { getItem, gossipItems, channelLabel, channels } from '@/mocks/gossip';
import { useSeo } from '@/hooks/useSeo';

export default function NewsDetail() {
  const { slug } = useParams<{ slug: string }>();
  const item = slug ? getItem(slug) : undefined;

  useSeo({
    title: item ? item.title : '内容不存在 | 吃瓜日报',
    description: item ? item.summary : '你访问的内容不存在，返回首页继续看瓜。',
    keywords: item ? `${channelLabel(item.channel)},${item.tags.join(',')},吃瓜日报` : '吃瓜日报',
    image: item?.cover || '',
    type: 'article',
    jsonLd: item
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: item.title,
            description: item.summary,
            author: { '@type': 'Person', name: item.author },
            datePublished: item.date,
            keywords: item.tags.join(','),
            articleSection: channelLabel(item.channel),
            publisher: { '@type': 'Organization', name: '吃瓜日报' },
            image: item.cover,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://chiguadaily.example/' },
              { '@type': 'ListItem', position: 2, name: channelLabel(item.channel), item: `https://chiguadaily.example/channel/${item.channel}` },
              { '@type': 'ListItem', position: 3, name: item.title },
            ],
          },
        ]
      : [],
  });

  if (!item) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="flex flex-1 flex-col items-center justify-center px-4 text-center">
          <h1 className="font-heading text-3xl font-black text-foreground-900">内容不存在或已下架</h1>
          <p className="mt-3 text-sm text-foreground-500">这条瓜可能已经被小编下架啦，去合集页看看别的吧。</p>
          <Link to="/daily" className="mt-6 inline-flex h-11 items-center rounded-full bg-primary-500 px-6 text-sm font-bold text-background-50 hover:bg-primary-600">
            去每日合集
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = gossipItems
    .filter((i) => i.slug !== item.slug)
    .sort((a, b) => {
      const sameA = a.channel === item.channel ? 0 : 1;
      const sameB = b.channel === item.channel ? 0 : 1;
      return sameA - sameB || (b.views - a.views);
    })
    .slice(0, 3);

  const chipClasses = 'inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-800 transition-colors hover:bg-primary-500 hover:text-background-50';

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-12">
          <nav className="text-xs text-foreground-400" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-700">首页</Link>
            <span className="mx-2">/</span>
            <Link to={`/channel/${item.channel}`} className="hover:text-primary-700">{channelLabel(item.channel)}</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-600">{item.title}</span>
          </nav>

          <div className="mt-6 grid gap-8 lg:grid-cols-3">
            <article className="lg:col-span-2 overflow-hidden rounded-2xl border border-background-200 bg-background-50">
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-background-200">
                <img src={item.cover} alt={item.title} className="h-full w-full object-cover" />
              </div>
              <div className="p-6 md:p-10">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/channel/${item.channel}`} className="rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50 hover:bg-primary-600">
                    {channelLabel(item.channel)}
                  </Link>
                  {item.hotTag && <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-black text-background-50">{item.hotTag}</span>}
                </div>
                <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                  {item.title}
                </h1>
                <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-background-200 py-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-pencil-line text-primary-600"></i>
                    {item.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {item.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>阅读约 {item.readTime} 分钟
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>{formatViews(item.views)} 阅读
                  </span>
                </div>

                <p className="mt-6 border-l-4 border-primary-300 bg-background-100 py-3 pl-4 text-base leading-7 text-foreground-700">
                  {item.summary}
                </p>

                <div className="mt-6 space-y-5 text-[15px] leading-8 text-foreground-800">
                  {item.content.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-bold text-foreground-500">相关标签</span>
                  {item.tags.map((t) => (
                    <Link key={t} to={`/daily?q=${encodeURIComponent(t)}`} className={chipClasses}>
                      # {t}
                    </Link>
                  ))}
                </div>

                <div className="mt-8 flex items-center justify-between gap-4 rounded-xl border border-background-200 bg-background-100 p-5">
                  <div className="flex items-center gap-3">
                    <span className="grid h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-lg text-background-50">
                      <i className="ri-quill-pen-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-bold text-foreground-900">{item.author}</p>
                      <p className="text-xs text-foreground-500">吃瓜日报编辑部 · 资料来源已尽量核对</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="grid h-9 w-9 items-center justify-center rounded-full border border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-accent-600" aria-label="收藏">
                      <i className="ri-heart-line"></i>
                    </button>
                    <button className="grid h-9 w-9 items-center justify-center rounded-full border border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600" aria-label="分享">
                      <i className="ri-share-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </article>

            <aside className="space-y-6 lg:sticky lg:top-20 lg:self-start">
              <HotRank />
              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-black text-foreground-900">相关推荐</h2>
                <div className="mt-3 space-y-3">
                  {related.map((r) => (
                    <Link key={r.slug} to={`/news/${r.slug}`} className="group flex gap-3">
                      <div className="relative aspect-square w-16 shrink-0 overflow-hidden rounded-lg bg-background-200">
                        <img src={r.cover} alt={r.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="line-clamp-2 text-sm font-bold leading-snug text-foreground-800 group-hover:text-primary-700">{r.title}</h3>
                        <p className="mt-1 text-xs text-foreground-400">{channelLabel(r.channel)} · {r.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-black text-foreground-900">切换频道</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {channels.map((c) => (
                    <Link key={c.key} to={`/channel/${c.key}`} className="rounded-full border border-background-200 bg-background-100 px-3 py-1.5 text-xs font-bold text-foreground-600 hover:border-primary-300 hover:text-primary-700">
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}